import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRepairOrders } from '../../hooks/useRepairOrders';
import { repairOrdersService } from '../../services/repairOrdersService';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import Card from '../../components/ui/Card';
import Breadcrumb from '../../components/ui/Breadcrumb';

const RepairOrdersList: React.FC = () => {
  const { orders, loading, error, refreshRepairOrders } = useRepairOrders();
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.repair_order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.customer && order.customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.device_brand && order.device_brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.device_model && order.device_model.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (order.device_serial && order.device_serial.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterStatus === 'all' || order.status === filterStatus;

    return matchesSearch && matchesFilter;
  });


  // Show loading while authentication is in progress or business is not loaded
  if (authLoading || (isAuthenticated && !businessId)) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <p className="text-gray-600">
          {authLoading ? 'Loading authentication...' : 'Loading business data...'}
        </p>
      </div>
    );
  }

  // Show message if user is authenticated but has no business
  if (isAuthenticated && !businessId) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Profile Required</h2>
          <p className="text-gray-600 mb-4">
            You need to create a business profile before you can manage repair orders.
          </p>
          <p className="text-sm text-gray-500">
            Please complete your business setup first.
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">
            Please log in to access the repair orders system.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Repair Orders', current: true }
  ];

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Repair Orders</h1>
        <Link to="/repair-orders/new">
          <Button variant="primary">
            Create New Repair Order
          </Button>
        </Link>
      </div>

      <div className="mb-6 space-y-4">
        <div className="max-w-md">
          <SearchInput
            placeholder="Search repair orders by number, customer, or device..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All Statuses' },
            { value: 'pending', label: 'Pending' },
            { value: 'process', label: 'In Process' },
            { value: 'completed', label: 'Completed' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterStatus(tab.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                filterStatus === tab.value
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ResponsiveTable
          headers={['Order Number', 'Customer', 'Device', 'Status', 'Date', 'Technician']}
          rows={filteredOrders.map(order => [
            order.repair_order_number,
            order.customer?.name || 'Unknown Customer',
            `${order.device_brand} ${order.device_model}`,
            <span className={`badge ${repairOrdersService.getStatusBadgeClass(order.status)}`}>
              {repairOrdersService.getStatusLabel(order.status)}
            </span>,
            new Date(order.created_at).toLocaleDateString(),
            order.technician?.name || 'Unassigned'
          ])}
          renderRow={(row, index) => {
            const order = filteredOrders[index];
            return (
              <tr
                key={order.id}
                onClick={() => navigate(`/repair-orders/${order.id}`)}
                className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[0]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[1]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[2]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[3]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[4]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[5]}
                </td>
              </tr>
            );
          }}
          renderMobileCard={(row, index) => {
            const order = filteredOrders[index];
            return (
              <Card
                key={order.id}
                onClick={() => navigate(`/repair-orders/${order.id}`)}
                className="mb-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                          {row[0]}
                        </h3>
                        <span className="text-sm font-medium text-gray-500">
                          {row[3]}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Customer</p>
                          <p className="text-sm font-medium text-gray-900">{row[1]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Device</p>
                          <p className="text-sm font-medium text-gray-900">{row[2]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Date</p>
                          <p className="text-sm font-medium text-gray-900">{row[4]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Technician</p>
                          <p className="text-sm font-medium text-gray-900">{row[5]}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          }}
          emptyMessage="No repair orders found"
        />
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {filteredOrders.length} of {orders.length} repair orders
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" disabled>
            Previous
          </Button>
          <Button variant="outline" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RepairOrdersList;